import { useQuery } from 'react-query';
import queryClient from '@/queryClient';
import { PaymentPartnerType } from '@/__generated';
import { apiSdk } from '@/httpClient';
const DEFAULT_PAGE_SIZE = 500;
// country
const all_countries_key = 'all_countries_key';

export const useGetAllCountries = (isOperatingOnly: boolean = true) => {
  return useQuery([all_countries_key, isOperatingOnly], async () => {
    return await apiSdk.CountryService.getCountries({
      pageSize: DEFAULT_PAGE_SIZE,
      orderBy: 'countryName',
      orderByAsc: true,
      isOperatingOnly: isOperatingOnly,
    });
  });
};
export const clearAllCountriesCache = () => queryClient.removeQueries(all_countries_key);

// city
const all_cities_key = 'all_cities_key';
export const useGetAllCities = () => {
  return useQuery('all_cities_key', async () => {
    return await apiSdk.CityService.getCities({
      pageSize: DEFAULT_PAGE_SIZE,
      orderBy: 'cityName',
      orderByAsc: true,
    });
  });
};
export const clearAllCitiesCache = () => queryClient.removeQueries(all_cities_key);

// region
const all_regions_key = 'all_regions_key';
export const useGetAllRegions = () => {
  return useQuery(all_regions_key, async () => {
    return await apiSdk.RegionService.getRegions({
      pageSize: DEFAULT_PAGE_SIZE,
      orderBy: 'regionName',
      orderByAsc: true,
    });
  });
};
export const clearAllRegionsCache = () => queryClient.removeQueries(all_regions_key);

// trip group
const all_tripgroups_key = 'all_tripgroups_key';
export const useGetAllTripGroups = () => {
  return useQuery(all_tripgroups_key, async () => {
    return await apiSdk.TripGroupService.getAutocompletionList();
  });
};
export const clearAllTripGroupsCache = () => queryClient.removeQueries(all_tripgroups_key);

// trip vendor
const all_tripvendors_key = 'all_tripvendors_key';
export const useGetAllTripVendors = () => {
  return useQuery(all_tripvendors_key, async () => {
    return await apiSdk.TransportVendorService.getAutocompletionList();
  });
};
export const clearAllTripVendorsCache = () => queryClient.removeQueries(all_tripvendors_key);

// trip vendor
const all_tariffParameter_key = 'all_tariffParameter_key';
export const useGetAllTariffParameters = () => {
  return useQuery(all_tariffParameter_key, async () => {
    return await apiSdk.TariffParameterService.getAutocompletionList();
  });
};
export const clearAllTariffParametersCache = () => queryClient.removeQueries(all_tariffParameter_key);

// service
const all_serviceConfiguration_key = 'all_serviceConfiguration_key';
export const useGetAllServiceConfigurations = () => {
  return useQuery(all_serviceConfiguration_key, async () => {
    return await apiSdk.ServiceConfigurationService.getAutoCompletion();
  });
};
export const clearAllServiceConfigurationsCache = () => queryClient.removeQueries(all_serviceConfiguration_key);

// service
const all_segments_key = 'all_segments_key';
export const useGetAllSegments = (baseTripId?: string | null | undefined) => {
  return useQuery(
    [all_segments_key, baseTripId],
    async () => {
      return await apiSdk.TripService.getSegmentAutocompletionList({ baseTripId: baseTripId || undefined });
    },
    { enabled: !!baseTripId },
  );
};
export const clearAllSegmentsCache = () => queryClient.removeQueries(all_segments_key);

// seating model
const all_seatingmodel_key = 'all_seatingmodel_key';
export const useGetAllSeatingModels = () => {
  return useQuery(all_seatingmodel_key, async () => {
    return await apiSdk.SeatingModelService.getAutocompletionList();
  });
};
export const clearAllSeatingModelsCache = () => queryClient.removeQueries(all_seatingmodel_key);

// route
const all_routes_key = 'all_routes_key';
export const useGetAllRoutes = () => {
  return useQuery([all_routes_key], async () => {
    return await apiSdk.RouteService.getAutocompletionList();
  });
};
export const clearAllRoutesCache = () => queryClient.removeQueries(all_routes_key);

// getReportingManagers
const all_reportingManagers_key = 'all_reportingManagers_key';
export const useGetAllReportingManagers = () => {
  return useQuery(all_reportingManagers_key, async () => {
    return await apiSdk.GlobalUserService.getReportingManagers({ pageSize: DEFAULT_PAGE_SIZE });
  });
};
export const clearAllReportingManagersCache = () => queryClient.removeQueries(all_reportingManagers_key);

// get global user by role
const all_global_user_key = 'all_global_user_key';
export const useGetAllGlobalUsers = (role: string) => {
  return useQuery([all_global_user_key, role], async () => {
    return await apiSdk.GlobalUserService.getUsersByRoles({ roles: [role] });
  });
};
export const clearAllGlobalUsersCache = () => queryClient.removeQueries(all_global_user_key);

// payment partner
const all_payment_partner_key = 'all_payment_partner_key';
export const useGetAlPaymentPartners = (partnerType: PaymentPartnerType) => {
  return useQuery([all_payment_partner_key, partnerType], async () => {
    return await apiSdk.PaymentPartnerService.getPaymentPartners({
      pageSize: DEFAULT_PAGE_SIZE,
      orderBy: 'partnerName',
      orderByAsc: true,
      partnerType: partnerType,
    });
  });
};
export const clearAllPaymentPartnersCache = () => queryClient.removeQueries(all_payment_partner_key);

// Partenaires du pays de la compagnie, pour ses comptes (droits compagnie, sans Manage Partners).
const company_payment_partner_key = 'company_payment_partner_key';
export const useGetCompanyPaymentPartners = (partnerType?: PaymentPartnerType) => {
  return useQuery([company_payment_partner_key, partnerType], async () => {
    return await apiSdk.PaymentPartnerService.companyPartners({ partnerType });
  });
};

// FareConfigurationsTariffParams
const all_FareConfigurationsTariffParams_key = 'all_FareConfigurationsTariffParams_key';
export const useGetAllSellOnTheGoFareConfigurationTariffParameters = () => {
  return useQuery(all_FareConfigurationsTariffParams_key, async () => {
    return await apiSdk.SellOnTheGoFareConfigurationService.getTariffParameters();
  });
};
export const clearAllSellOnTheGoFareConfigurationTariffParametersCache = () => queryClient.removeQueries(all_FareConfigurationsTariffParams_key);

// EmployeeCategory
const all_employee_categories_key = 'all_employee_categories_key';
export const useGetAllEmployeeCategories = () => {
  return useQuery(all_employee_categories_key, async () => {
    return await apiSdk.EmployeeCategoryService.getAutocompletionList();
  });
};
export const clearAllEmployeeCategoriesCache = () => queryClient.removeQueries(all_employee_categories_key);

// company users
const all_company_users_key = 'all_company_users_key';
export const useGetAllCompanyUsers = (role: string) => {
  return useQuery([all_company_users_key, role], async () => {
    return await apiSdk.CompanyUserService.getUserByRoles({ roles: [role] });
  });
};
export const clearAllCompanyUsersCache = () => queryClient.removeQueries(all_company_users_key);

// company ticket
const all_company_ticket_key = 'all_company_ticket_key';
export const useGetAllCompanyTickets = () => {
  return useQuery(all_company_ticket_key, async () => {
    return await apiSdk.CompanyTicketClassService.getAutocompletionList();
  });
};
export const clearAllCompanyTicketsCache = () => queryClient.removeQueries(all_company_ticket_key);

// bus models
const all_bus_model_key = 'all_bus_model_key';
export const useGetAllBusModels = () => {
  return useQuery(all_bus_model_key, async () => {
    return await apiSdk.BusModelService.getAutocompletionList();
  });
};
export const clearAllBusModelsCache = () => queryClient.removeQueries(all_bus_model_key);

// bus models
const all_bus_insurer_key = 'all_bus_insurer_key';
export const useGetAllBussInsurers = () => {
  return useQuery(all_bus_insurer_key, async () => {
    return await apiSdk.BusInsurerService.getAutocompletionList();
  });
};
export const clearAllBussInsurersCache = () => queryClient.removeQueries(all_bus_insurer_key);
// bus
const all_bus_key = 'all_bus_key';
export const useGetAllBusses = () => {
  return useQuery(all_bus_key, async () => {
    return await apiSdk.BusService.getBuses({
      pageSize: DEFAULT_PAGE_SIZE,
      orderBy: 'busName',
    });
  });
};
export const clearAllBussesCache = () => queryClient.removeQueries(all_bus_key);

// bus models
const all_bus_brands_key = 'all_bus_brands_key';
export const useGetAllBussBrands = () => {
  return useQuery(all_bus_brands_key, async () => {
    return await apiSdk.BusBrandService.getAutocompletionList();
  });
};
export const clearAllBussBrandsCache = () => queryClient.removeQueries(all_bus_brands_key);

// base ticket class
const all_base_ticket_classes = 'all_base_ticket_classes';
export const useGetAllBaseTicketClasses = () => {
  return useQuery(all_base_ticket_classes, async () => {
    return await apiSdk.TicketClassService.getAutocompletionList();
  });
};
export const clearAllBaseTicketClassesCache = () => queryClient.removeQueries(all_base_ticket_classes);

// base ticket class
const all_base_trips = 'all_base_trips';
export const useGetAllBaseTrips = (routeId: string | null | undefined) => {
  return useQuery(
    [all_base_trips, routeId],
    async () => {
      return await apiSdk.TripService.getAutocompletionList({
        routeId: routeId || undefined,
        limit: DEFAULT_PAGE_SIZE,
      });
    },
    { enabled: !!routeId },
  );
};
export const clearAllBaseTripsCache = () => queryClient.removeQueries(all_base_trips);

// base ticket class
const all_stations = 'all_stations';
export const useGetAllStations = () => {
  return useQuery(all_stations, async () => {
    return await apiSdk.StationService.getAutocompletionList({
      limit: DEFAULT_PAGE_SIZE,
    });
  });
};
export const clearAllStationsCache = () => queryClient.removeQueries(all_stations);

// base ticket class
const get_all_base_tariffs = 'get_all_base_tariff';
export const useGetAllBaseTariffParameter = () => {
  return useQuery(get_all_base_tariffs, async () => {
    return await apiSdk.BaseTariffParameterService.getAutocompletionList();
  });
};
